import { useState, useMemo } from 'react';
import { GLOSSARY_TERMS, getGlossaryAudioUrl } from '../lib/glossaryTerms';
import SectionBanner from '../components/SectionBanner';

/**
 * Glossary — pagina dedicada para navegar el catalogo completo de terminos.
 * Diseno minimalista: buscador grande + chips de categoria + grid de cards.
 *
 * UX:
 *   - Buscador filtra por term, definicion, ejemplo (substring case-insensitive,
 *     sin tildes para ser tolerante)
 *   - Chips de categoria filtran por seccion (Basicos, Fundamental, etc.)
 *   - Click en card = expande la definicion completa inline (sin modal — la pagina
 *     YA es el detalle, no necesita popup adicional)
 *   - Audio play directo en cada card
 *   - Responsive: 1 col movil, 2 col tablet, 3 col desktop
 */

const CATEGORIES = [
  { id: 'all', label: 'Todos' },
  { id: 'basicos', label: 'Básicos' },
  { id: 'fundamental', label: 'Fundamental' },
  { id: 'tecnico', label: 'Técnico' },
  { id: 'cerebro', label: 'Cerebro 2.0' },
  { id: 'cripto', label: 'Cripto · DeFi' },
  { id: 'colombia', label: 'Colombia' },
];

function normalize(text) {
  return (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export default function Glossary() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [openSlugs, setOpenSlugs] = useState(new Set());

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return GLOSSARY_TERMS.filter(t => {
      if (category !== 'all' && t.category !== category) return false;
      if (!q) return true;
      const haystack = normalize(`${t.term} ${t.definition} ${t.example}`);
      return haystack.includes(q);
    });
  }, [search, category]);

  const toggleOpen = (slug) => {
    setOpenSlugs(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Visual: .glosario-ola + tokens --fx-* (glass Ola A). Solo chrome; search/filter intactos.
  return (
    <div className="glosario-ola px-4 sm:px-10 py-6 max-w-6xl">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-[#181D27] mb-1">Glosario</h1>
        <p className="text-sm text-[#6B7280]">
          {GLOSSARY_TERMS.length} términos financieros explicados en lenguaje claro, con ejemplos y audio.
        </p>
      </div>

      <SectionBanner
        id="glossary"
        title="Ninguna palabra rara sin explicar"
        text="Busca cualquier término aquí, o tócalo subrayado dentro de un análisis: definición en dos frases, ejemplo real y audio de 10 segundos."
      />

      {/* Search bar */}
      <div className="relative mb-3">
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar un término... (ej: moat, ETF, P/E)"
          className="w-full pl-10 pr-4 py-3 bg-white border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#2A59C6] focus:ring-2 focus:ring-[#2A59C6]/20 transition"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            aria-label="Limpiar"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] text-lg"
          >
            ×
          </button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition flex-shrink-0 ${
              category === c.id
                ? 'bg-[#2A59C6] text-white'
                : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:border-[#2A59C6] hover:text-[#2A59C6]'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Count */}
      <div className="text-xs text-[#9CA3AF] mb-3">
        {filtered.length} {filtered.length === 1 ? 'término' : 'términos'}
        {search && ` para "${search}"`}
        {category !== 'all' && ` en ${CATEGORIES.find(c => c.id === category)?.label}`}
      </div>

      {/* Grid de cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#9CA3AF]">
          <div className="text-4xl mb-2">🔍</div>
          <p className="text-sm">No encontramos términos que coincidan.</p>
          <p className="text-xs mt-1">Prueba con otra búsqueda o cambia la categoría.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(t => (
            <GlossaryCard
              key={t.slug}
              term={t}
              isOpen={openSlugs.has(t.slug)}
              onToggle={() => toggleOpen(t.slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * GlossaryCard — card individual con term + preview. Click expande detalle inline.
 */
function GlossaryCard({ term, isOpen, onToggle }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:border-[#2A59C6]/40 transition">
      <button
        onClick={onToggle}
        className="w-full text-left p-4 hover:bg-[#FAFBFF] transition"
        type="button"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="text-sm font-bold text-[#181D27]">{term.term}</span>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={`transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <p className="text-xs text-[#6B7280] leading-snug line-clamp-2">
          {term.definition}
        </p>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-[#F3F4F6] pt-3">
          {/* Definicion completa */}
          <p className="text-xs text-[#374151] leading-relaxed mb-3">{term.definition}</p>

          {/* Ejemplo */}
          <div className="bg-[#F0F4FF] rounded-lg p-2.5 mb-3 border border-[#DBEAFE]">
            <div className="text-[9px] uppercase tracking-wider font-semibold text-[#2A59C6] mb-0.5">Ejemplo</div>
            <p className="text-xs text-[#374151] leading-relaxed">{term.example}</p>
          </div>

          {/* Audio */}
          <CardAudio slug={term.slug} />
        </div>
      )}
    </div>
  );
}

function CardAudio({ slug }) {
  const [playing, setPlaying] = useState(false);
  const audioUrl = getGlossaryAudioUrl(slug);

  // Cada card maneja su propio audio element para que se puedan pausar al cerrar
  let audioEl = null;

  const handlePlay = () => {
    if (!audioEl) return;
    if (playing) audioEl.pause();
    else audioEl.play().catch(() => {});
  };

  return (
    <div className="bg-gradient-to-br from-[#0a2540] to-[#1a3a6c] rounded-lg p-2.5 text-white flex items-center gap-2.5">
      <button
        onClick={handlePlay}
        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center flex-shrink-0 transition"
        aria-label={playing ? 'Pausar' : 'Reproducir'}
        type="button"
      >
        {playing ? (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
        ) : (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>
      <div className="text-[10px] opacity-90 flex-1">Escuchar definición</div>
      <audio
        ref={(el) => { audioEl = el; }}
        src={audioUrl}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        style={{ display: 'none' }}
      />
    </div>
  );
}
